import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { coffeeRecipes } from '../data/coffeeRecipes';

const { FiArrowDown, FiCheck, FiX, FiCoffee, FiHelpCircle } = FiIcons;

function TutorialMode({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [tutorialComplete, setTutorialComplete] = useState(false);

  // Tutorial recipe: Simple Latte
  const tutorialRecipe = coffeeRecipes.latte;
  const requiredSequence = tutorialRecipe.sequence;
  const currentRequired = requiredSequence[currentStep];

  const handleIngredientClick = (ingredientId) => {
    if (ingredientId === currentRequired) {
      // Correct ingredient
      setSelectedIngredients(prev => [...prev, ingredientId]);
      setCurrentStep(prev => prev + 1);
      setShowHint(false);

      // Check if tutorial is complete
      if (currentStep + 1 >= requiredSequence.length) {
        setTimeout(() => {
          setTutorialComplete(true);
        }, 1000);
      }
    } else {
      // Wrong ingredient - show feedback
      setShowHint(true);
      setTimeout(() => setShowHint(false), 2000);
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  if (tutorialComplete) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl text-center max-w-sm w-full"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">Perfect Latte!</h2>
          <p className="text-gray-600 mb-6">
            You've mastered the basics! Now you're ready to serve real customers and learn more recipes.
          </p>
          <motion.button
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-3 rounded-2xl font-bold w-full"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleComplete}
          >
            Start Playing!
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen bg-gradient-to-b from-amber-50 to-orange-100 overflow-hidden">
      {/* Tutorial Header - Reduced height and repositioned */}
      <div className="absolute top-2 left-2 right-2 z-20">
        <div className="bg-blue-500 text-white rounded-xl p-3 shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-bold">Tutorial: Making a Latte</h3>
            <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
              Step {currentStep + 1}/{requiredSequence.length}
            </span>
          </div>
          
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-xs mb-1">Next step:</p>
            <div className="flex items-center">
              <SafeIcon icon={FiArrowDown} className="text-yellow-300 mr-2 text-sm" />
              <span className="font-medium capitalize text-sm">
                {currentRequired?.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-2">
            <div className="flex justify-between text-xs mb-1">
              <span>Progress</span>
              <span>{currentStep}/{requiredSequence.length}</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <motion.div
                className="bg-yellow-300 h-1.5 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / requiredSequence.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Coffee Shop Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 right-0 h-3/5 bg-gradient-to-b from-amber-100 to-amber-200">
          <div className="absolute top-6 left-2 right-2 h-4 bg-gradient-to-r from-amber-800 to-amber-900 rounded shadow-lg"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-t from-amber-900 to-amber-700 shadow-2xl">
          <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-amber-600 to-amber-700 rounded-t-lg"></div>
        </div>
      </div>

      {/* Tutorial Equipment - Better positioning and visibility */}
      <div className="absolute inset-0 z-10">
        {/* Espresso Machine - Moved down and made more visible */}
        <motion.button
          className={`absolute w-20 h-24 rounded-lg shadow-2xl z-15 ${
            currentRequired === 'espresso' 
              ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 ring-4 ring-yellow-300 ring-opacity-75' 
              : 'bg-gradient-to-b from-gray-700 to-gray-800'
          }`}
          style={{ left: '15%', top: '30%' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleIngredientClick('espresso')}
          animate={currentRequired === 'espresso' ? { 
            boxShadow: ['0 0 0 0 rgba(255, 255, 0, 0.7)', '0 0 0 15px rgba(255, 255, 0, 0)'] 
          } : {}}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <div className="absolute inset-1 bg-black/20 rounded flex items-center justify-center">
            <span className="text-white text-2xl">☕</span>
          </div>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-800 text-center whitespace-nowrap font-medium bg-white/80 px-2 py-1 rounded">
            Espresso
          </div>
        </motion.button>

        {/* Milk Steamer - Better positioning */}
        <motion.button
          className={`absolute w-18 h-22 rounded-lg shadow-2xl z-15 ${
            currentRequired === 'steamed_milk' 
              ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 ring-4 ring-yellow-300 ring-opacity-75' 
              : 'bg-gradient-to-b from-blue-400 to-blue-600'
          }`}
          style={{ left: '50%', top: '28%' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleIngredientClick('steamed_milk')}
          animate={currentRequired === 'steamed_milk' ? { 
            boxShadow: ['0 0 0 0 rgba(255, 255, 0, 0.7)', '0 0 0 15px rgba(255, 255, 0, 0)'] 
          } : {}}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <div className="absolute inset-1 bg-black/20 rounded flex items-center justify-center">
            <span className="text-white text-xl">🥛</span>
          </div>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-800 text-center whitespace-nowrap font-medium bg-white/80 px-2 py-1 rounded">
            Steamed Milk
          </div>
        </motion.button>

        {/* Light Foam - Better positioning */}
        <motion.button
          className={`absolute w-16 h-20 rounded-lg shadow-2xl z-15 ${
            currentRequired === 'light_foam' 
              ? 'bg-gradient-to-b from-yellow-400 to-yellow-600 ring-4 ring-yellow-300 ring-opacity-75' 
              : 'bg-gradient-to-b from-gray-100 to-gray-300'
          }`}
          style={{ left: '75%', top: '29%' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleIngredientClick('light_foam')}
          animate={currentRequired === 'light_foam' ? { 
            boxShadow: ['0 0 0 0 rgba(255, 255, 0, 0.7)', '0 0 0 15px rgba(255, 255, 0, 0)'] 
          } : {}}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <div className="absolute inset-1 bg-black/20 rounded flex items-center justify-center">
            <span className="text-gray-700 text-lg">🫧</span>
          </div>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-800 text-center whitespace-nowrap font-medium bg-white/80 px-2 py-1 rounded">
            Light Foam
          </div>
        </motion.button>
      </div>

      {/* Coffee Cup - Better positioning and visibility */}
      <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-15">
        <div className="relative">
          <div className="w-24 h-28 bg-gradient-to-b from-gray-100 to-gray-200 rounded-b-3xl border-4 border-gray-300 relative overflow-hidden shadow-xl">
            {/* Liquid layers */}
            <AnimatePresence>
              {selectedIngredients.includes('espresso') && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-800 to-amber-600"
                  initial={{ height: 0 }}
                  animate={{ height: '25%' }}
                  transition={{ duration: 0.5 }}
                />
              )}
              {selectedIngredients.includes('steamed_milk') && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-100 to-white"
                  initial={{ height: '25%' }}
                  animate={{ height: '80%' }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                />
              )}
              {selectedIngredients.includes('light_foam') && (
                <motion.div
                  className="absolute top-0 left-0 right-0 bg-gradient-to-b from-gray-50 to-white h-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                />
              )}
            </AnimatePresence>
          </div>
          <div className="w-6 h-4 bg-gray-300 rounded-r-lg absolute right-0 top-1/2 transform -translate-y-1/2"></div>
        </div>
      </div>

      {/* Feedback System */}
      <AnimatePresence>
        {showHint && (
          <motion.div
            className="absolute top-1/2 left-2 right-2 bg-red-500 text-white rounded-xl p-4 shadow-xl z-30"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="flex items-center">
              <SafeIcon icon={FiX} className="text-xl mr-3" />
              <div>
                <p className="font-bold">Wrong ingredient!</p>
                <p className="text-sm">Look for the glowing equipment. Follow the sequence!</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Button */}
      <motion.button
        className="absolute bottom-4 right-4 bg-blue-500 text-white p-3 rounded-full shadow-lg z-20"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowHint(!showHint)}
      >
        <SafeIcon icon={FiHelpCircle} className="text-xl" />
      </motion.button>

      {/* Skip Tutorial */}
      <motion.button
        className="absolute bottom-4 left-4 bg-gray-500 text-white px-4 py-2 rounded-full shadow-lg z-20 text-sm"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleComplete}
      >
        Skip Tutorial
      </motion.button>

      {/* Visual Guide Arrows - Point to current equipment */}
      <AnimatePresence>
        {currentRequired && (
          <motion.div
            className="absolute z-25 pointer-events-none"
            style={{
              left: currentRequired === 'espresso' ? '25%' : 
                    currentRequired === 'steamed_milk' ? '58%' : '83%',
              top: currentRequired === 'espresso' ? '20%' : 
                   currentRequired === 'steamed_milk' ? '18%' : '19%'
            }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ 
              opacity: 1, 
              y: [0, -10, 0],
              scale: [1, 1.1, 1]
            }}
            exit={{ opacity: 0 }}
            transition={{ 
              y: { duration: 1.5, repeat: Infinity },
              scale: { duration: 1.5, repeat: Infinity }
            }}
          >
            <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
              👆 Tap Here!
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TutorialMode;